import { mdiArrowDownBold, mdiCash, mdiPercent, mdiWrench } from '@mdi/js';
import { Button } from '@mollybet/ui';
import { api } from 'api';
import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { TableTitle } from 'common/components/tables';
import { CurrencyCodes } from 'common/configs/currency';
import { AccountMovement, Xrates } from 'common/state/accounts/actions';
import {
  AdjustmentTypes,
  requestAccountMovements,
} from 'common/state/accounts/thunkActions';
import { Customer } from 'common/state/customers/actions';
import { ProviderSummary } from 'common/state/provider/actions';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ModalModes } from '../provider';
import { AdjustmentModalProps } from './adjustmentModal';

export interface AdjustmentsTableProps {
  customer?: Customer;
  summaries?: ProviderSummary[];
  allSummaries?: ProviderSummary[];
  xrates?: Xrates;
  setModalMode: (mode: ModalModes) => void;
  setInitialAdjustmentData: (
    data: AdjustmentModalProps['initialData'] | null
  ) => void;
  isLoading?: boolean;
  error?: React.ReactNode;
}

interface TransactionSplit {
  account: string;
  amount: string;
  amount_xn: string;
  currency: CurrencyCodes;
  description?: string;
  id: number;
}

interface Transaction {
  type: AdjustmentTypes;
  cr_date: string;
  currency: CurrencyCodes;
  description: string;
  id: number;
  post_date: string;
  splits: TransactionSplit[];
}

interface AdjustmentMovement extends Omit<AccountMovement, 'transaction'> {
  sender: TransactionSplit;
  reciever?: TransactionSplit;
  transaction: Transaction;
}

const Spacer = styled.div`
  display: inline-block;
  width: 2rem;
`;

export interface AdjustmentRow extends AdjustmentMovement, ProviderSummary {
  client_account_code: string;
}

export const AdjustmentsTable = ({
  customer,
  summaries,
  allSummaries,
  xrates,
  setModalMode,
  setInitialAdjustmentData,
  isLoading,
  error,
}: AdjustmentsTableProps) => {
  const [totusDepositRows, setTotusDepositRows] = useState<AdjustmentRow[]>([]);
  const [customerDepositRows, setCustomerDepositRows] = useState<
    AdjustmentRow[]
  >([]);
  const [plSettledRows, setPLSettledRows] = useState<AdjustmentRow[]>([]);
  const [singBetRows, setSingBetRows] = useState<AdjustmentRow[]>([]);
  const [commissionRows, setCommissionRows] = useState<AdjustmentRow[]>([]);
  const [adjustmentRows, setAdjustmentRows] = useState<AdjustmentRow[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const currencies = useMemo(
    () =>
      Array.from(new Set(summaries?.map((summary) => summary.ccy_code))).sort(),
    [summaries]
  );

  useEffect(() => {
    if (!summaries?.length) {
      setTotusDepositRows([]);
      setCustomerDepositRows([]);
      setPLSettledRows([]);
      setSingBetRows([]);
      setCommissionRows([]);
      setAdjustmentRows([]);
      return;
    }

    setMovementsLoading(true);

    Promise.all(
      summaries
        .map((summary) => [
          requestAccountMovements(
            `provider.${summary.id}.provider_adjustment.${summary.ccy_code}`,
            {
              limit: 1000,
            }
          ).then((response) =>
            response.data.results.map((movement) => ({
              client_account_code: `provider.${summary.id}.provider_adjustment.${summary.ccy_code}`,
              ...summary,
              ...movement,
            }))
          ),
          requestAccountMovements(
            `provider.${summary.id}.totus_bookie_deposits.${summary.ccy_code}`,
            {
              limit: 1000,
            }
          ).then((response) =>
            response.data.results.map((movement) => ({
              client_account_code: `provider.${summary.id}.totus_bookie_deposits.${summary.ccy_code}`,
              ...summary,
              ...movement,
            }))
          ),
          requestAccountMovements(
            `provider.${summary.id}.customer_bookie_deposits.${summary.ccy_code}`,
            {
              limit: 1000,
            }
          ).then((response) =>
            response.data.results.map((movement) => ({
              client_account_code: `provider.${summary.id}.customer_bookie_deposits.${summary.ccy_code}`,
              ...summary,
              ...movement,
            }))
          ),
          requestAccountMovements(
            `provider.${summary.id}.pl_settled.${summary.ccy_code}`,
            {
              limit: 1000,
            }
          ).then((response) =>
            response.data.results.map((movement) => ({
              client_account_code: `provider.${summary.id}.pl_settled.${summary.ccy_code}`,
              ...summary,
              ...movement,
            }))
          ),
          requestAccountMovements(
            `provider.${summary.id}.singbet_dividend.${summary.ccy_code}`,
            {
              limit: 1000,
            }
          ).then((response) =>
            response.data.results.map((movement) => ({
              client_account_code: `provider.${summary.id}.singbet_dividend.${summary.ccy_code}`,
              ...summary,
              ...movement,
            }))
          ),
          requestAccountMovements(
            `provider.${summary.id}.commission_settled.${summary.ccy_code}`,
            {
              limit: 1000,
            }
          ).then((response) =>
            response.data.results.map((movement) => ({
              client_account_code: `provider.${summary.id}.commission_settled.${summary.ccy_code}`,
              ...summary,
              ...movement,
            }))
          ),
        ])
        .flat()
    )
      .then((responses) =>
        responses.flat().sort((rowA, rowB) => {
          if (rowA.post_date > rowB.post_date) return -1;
          if (rowA.post_date < rowB.post_date) return 1;
          return 0;
        })
      )
      .then((rows) => {
        Promise.all(
          rows.map((row) =>
            api
              .get<Transaction>(row.transaction)
              .then((response) => response.data)
              .then((transaction) => {
                const _row: Partial<AdjustmentRow> = {
                  ...row,
                  transaction: transaction,
                };
                _row.sender = transaction.splits.find(
                  (split) =>
                    split.account.includes(`provider.${row.id}`) &&
                    !split.account.includes('adjustments')
                );
                _row.reciever = transaction.splits.find(
                  (split) =>
                    !split.account.includes(`provider.${row.id}`) &&
                    !split.account.includes('adjustments')
                );
                return _row as AdjustmentRow;
              })
          )
        ).then((rows) => {
          const _totusDepositRows: AdjustmentRow[] = [];
          const _customerDepositRows: AdjustmentRow[] = [];
          const _plSettledRows: AdjustmentRow[] = [];
          const _singBetRows: AdjustmentRow[] = [];
          const _commissionRows: AdjustmentRow[] = [];
          const _adjustmentRows: AdjustmentRow[] = [];

          rows.forEach((row) => {
            if (row.client_account_code.includes('totus_bookie_deposits'))
              _totusDepositRows.push(row);
            if (row.client_account_code.includes('customer_bookie_deposits'))
              _customerDepositRows.push(row);
            if (row.client_account_code.includes('pl_settled'))
              _plSettledRows.push(row);
            if (row.client_account_code.includes('singbet_dividend'))
              _singBetRows.push(row);
            if (row.client_account_code.includes('commission_settled'))
              _commissionRows.push(row);
            if (row.client_account_code.includes('provider_adjustment'))
              _adjustmentRows.push(row);
          });

          setTotusDepositRows(_totusDepositRows);
          setCustomerDepositRows(_customerDepositRows);
          setPLSettledRows(_plSettledRows);
          setSingBetRows(_singBetRows);
          setCommissionRows(_commissionRows);
          setAdjustmentRows(_adjustmentRows);
          setMovementsLoading(false);
        });
      });
  }, [summaries]);

  const columns = useMemo(
    (): ColumnProp<AdjustmentRow>[] => [
      {
        key: 'date',
        title: 'Date',
        valueFormatter: (row) => new Date(row.post_date).toLocaleString(),
      },
      { key: 'description', title: 'Description' },
      {
        key: 'contact',
        title: 'Contact',
        valueFormatter: () =>
          customer
            ? `${customer.first_name} ${customer.last_name}`
            : 'Loading...',
      },
      {
        key: 'account',
        title: 'Account',
        valueFormatter: (row) => row.username,
      },
      {
        key: 'destination',
        title: 'Destination',
        valueFormatter: (row) => {
          const id = parseInt(
            row?.reciever?.account?.match(/customer.([\d]+)/)?.[1]
          );
          if (!id) return '-';
          return allSummaries?.find((summary) => summary.id)?.id || '-';
        },
      },
      { key: 'currency', title: 'Currency' },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row) =>
          new Currency(row.amount, row.currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({ symbol: false }),
        sortValueGenerator: (row) => parseFloat(row.amount),
      },
      {
        key: 'customer_currency',
        title: 'Customer Currency',
        valueFormatter: () =>
          customer ? customer.default_currency : 'Loading...',
      },
      {
        key: 'customer_amount',
        title: 'Customer Amount',
        valueFormatter: (row) =>
          customer && xrates
            ? new Currency(
                (xrates[customer.default_currency] * parseFloat(row.amount)) /
                  xrates[row.currency],
                customer.default_currency
              ).toElement()
            : 'Loading...',
        csvFormatter: (row) =>
          customer && xrates
            ? new Currency(
                (xrates[customer.default_currency] * parseFloat(row.amount)) /
                  xrates[row.currency],
                customer.default_currency
              ).toString({ symbol: false })
            : 'Loading...',
        sortValueGenerator: (row) =>
          customer && xrates
            ? (xrates[customer.default_currency] * parseFloat(row.amount)) /
              xrates[row.currency]
            : 'Loading...',
      },
      {
        key: 'rate',
        title: 'Rate',
        valueFormatter: (row) =>
          customer && xrates
            ? (
                xrates[customer.default_currency] / xrates[row.ccy_code]
              ).toPrecision(4)
            : 'Loading...',
      },
      {
        key: 'actions',
        title: 'Actions',
        valueFormatter: (row: AdjustmentRow) => (
          <Button
            onClick={() => {
              const recieverAccount = row.reciever?.account
                ?.split('/')
                ?.filter((part) => !!part)
                ?.slice(-1)?.[0];
              const senderAccount = row.sender?.account
                ?.split('/')
                ?.filter((part) => !!part)
                ?.slice(-1)?.[0];
              // Adjusting transfer
              if (row.sender && row.reciever) {
                setInitialAdjustmentData({
                  sourceClientCode: recieverAccount,
                  destinationClientCode: senderAccount,
                  sourceAmount: `${row.reciever.amount}`,
                  description:
                    'Reverting Adjusting Transfer: ' +
                    `${new Date(row.post_date).toISOString().slice(0, 19)} ` +
                    `${row.sender.currency} ${row.sender.amount} (${row.sender.id})`,
                  type: AdjustmentTypes.ADJUSTING_TRANSFER,
                });
                // Normal Adjustment
              } else {
                setInitialAdjustmentData({
                  sourceClientCode: senderAccount,
                  sourceAmount: `${-row.sender.amount}`,
                  description:
                    'Reverting Adjustment: ' +
                    `${new Date(row.post_date).toISOString().slice(0, 19)} ` +
                    `${row.sender.currency} ${row.sender.amount} (${row.sender.id})`,
                  type: AdjustmentTypes.ADJUSTMENT,
                });
              }
              setModalMode('adjustment');
            }}
          >
            Revert
          </Button>
        ),
      },
    ],
    [customer, setInitialAdjustmentData, setModalMode, allSummaries, xrates]
  );

  const adjustGenerator = useCallback(
    (
        type:
          | 'provider_adjustment'
          | 'totus_bookie_deposits'
          | 'customer_bookie_deposits'
          | 'pl_settled'
          | 'singbet_dividend'
          | 'commission_settled',
        currency: CurrencyCodes
      ) =>
      () => {
        if (summaries?.length) {
          setInitialAdjustmentData({
            sourceClientCode: `provider.${summaries[0].id}.${type}.${currency}`,
          });
          setModalMode('adjustment');
        }
      },
    [summaries, setInitialAdjustmentData, setModalMode]
  );

  return (
    <>
      {currencies.map((currency) => (
        <React.Fragment key={`totus-deposits-${currency}`}>
          <TableTitle
            icon={mdiArrowDownBold}
            title={
              <>
                Totus Deposits {currency}
                <Spacer />
                <Button
                  onClick={adjustGenerator('totus_bookie_deposits', currency)}
                >
                  Adjust
                </Button>
              </>
            }
          />
          <BaseTable
            columns={columns}
            rows={
              totusDepositRows.filter((row) => row.ccy_code === currency) || []
            }
            csvName={`Totus Deposits ${currency}`}
            isLoading={isLoading || movementsLoading}
            error={error}
            paginate={false}
          />
        </React.Fragment>
      ))}
      {currencies.map((currency) => (
        <React.Fragment key={`customer-deposits-${currency}`}>
          <TableTitle
            icon={mdiArrowDownBold}
            title={
              <>
                Customer Deposits {currency}
                <Spacer />
                <Button
                  onClick={adjustGenerator(
                    'customer_bookie_deposits',
                    currency
                  )}
                >
                  Adjust
                </Button>
              </>
            }
          />
          <BaseTable
            columns={columns}
            rows={
              customerDepositRows.filter((row) => row.ccy_code === currency) ||
              []
            }
            csvName={`Customer Deposits ${currency}`}
            isLoading={isLoading || movementsLoading}
            error={error}
            paginate={false}
          />
        </React.Fragment>
      ))}
      {currencies.map((currency) => (
        <React.Fragment key={`profit-loss-settled-${currency}`}>
          <TableTitle
            icon={mdiCash}
            title={
              <>
                Profit/Loss Settled {currency}
                <Spacer />
                <Button onClick={adjustGenerator('pl_settled', currency)}>
                  Adjust
                </Button>
              </>
            }
          />
          <BaseTable
            columns={columns}
            rows={
              plSettledRows.filter((row) => row.ccy_code === currency) || []
            }
            csvName={`Profit/Loss Settled ${currency}`}
            isLoading={isLoading || movementsLoading}
            error={error}
            paginate={false}
          />
        </React.Fragment>
      ))}
      {customer?.username === 'drinbet' && (
        <>
          {currencies.map((currency) => (
            <React.Fragment key={`araco-singbet-dividend-${currency}`}>
              <TableTitle
                icon={mdiCash}
                title={
                  <>
                    Araco SingBet - `dividend` {currency}
                    <Spacer />
                    <Button
                      onClick={adjustGenerator('singbet_dividend', currency)}
                    >
                      Adjust
                    </Button>
                  </>
                }
              />
              <BaseTable
                columns={columns}
                rows={
                  singBetRows.filter((row) => row.ccy_code === currency) || []
                }
                csvName={`Araco SingBet - \`dividend\` ${currency}`}
                isLoading={isLoading || movementsLoading}
                error={error}
                paginate={false}
              />
            </React.Fragment>
          ))}
        </>
      )}
      {currencies.map((currency) => (
        <React.Fragment key={`commission-settled-${currency}`}>
          <TableTitle
            icon={mdiPercent}
            title={
              <>
                Commission Settled {currency}
                <Spacer />
                <Button
                  onClick={adjustGenerator('commission_settled', currency)}
                >
                  Adjust
                </Button>
              </>
            }
          />
          <BaseTable
            columns={columns}
            rows={
              commissionRows.filter((row) => row.ccy_code === currency) || []
            }
            csvName={`Commission Settled ${currency}`}
            isLoading={isLoading || movementsLoading}
            error={error}
            paginate={false}
          />
        </React.Fragment>
      ))}
      {currencies.map((currency) => (
        <React.Fragment key={`adjustments-${currency}`}>
          <TableTitle
            icon={mdiWrench}
            title={
              <>
                Adjustments {currency}
                <Spacer />
                <Button
                  onClick={adjustGenerator('provider_adjustment', currency)}
                >
                  Adjust
                </Button>
              </>
            }
          />
          <BaseTable
            columns={columns}
            rows={
              adjustmentRows.filter((row) => row.ccy_code === currency) || []
            }
            csvName={`Adjustments ${currency}`}
            isLoading={isLoading || movementsLoading}
            error={error}
            paginate={false}
          />
        </React.Fragment>
      ))}
    </>
  );
};
